import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Truck, Star, Clock, ShoppingCart, Heart } from 'lucide-react';
import { getImageUrl } from '../utils/helpers';
import { VariantSelectionModal } from './VariantSelectionModal';
import { PriceBlock, productCompare, discountPct } from './pricing';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { trackAddToCart } from '../utils/analytics';

const calculateDeliveryTime = (distanceKm) => {
  if (!distanceKm || distanceKm <= 0) return null;
  return Math.round(distanceKm * 3 + 5);
};

const formatDeliveryTime = (minutes) => {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
};

const ProductCard = ({ product, onAdClick }) => {
  const productId = product._id || product.id;
  const productSlug = product.slug || productId;
  const isAd = product.is_ad || false;
  const { addToCart } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { user } = useAuth();
  const liked = isFavorite(productId);
  const [variantOpen, setVariantOpen] = useState(false);
  const hasVariants = (product.variants || []).length > 0;
  const compare = productCompare(product);
  const off = discountPct(product.price, compare);

  const deliveryTime = product.delivery_time_minutes || calculateDeliveryTime(product.distance_km);
  const formattedTime = formatDeliveryTime(deliveryTime);

  const handleClick = () => {
    if (isAd && product.campaign_id && onAdClick) {
      onAdClick(product.campaign_id);
    }
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasVariants) { setVariantOpen(true); return; }
    addToCart(product, 1);
    trackAddToCart(product, 1);
    toast({
      title: 'Agregado al carrito',
      description: product.title,
    });
  };

  const handleToggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast({ title: 'Inicia sesion para guardar favoritos', variant: 'destructive' });
      return;
    }
    toggleFavorite(productId);
  };

  return (
    <>
      <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer h-full flex flex-col">
        <div className="relative">
          <Link to={`/product/${productSlug}`} onClick={handleClick} data-testid={`product-card-image-link-${productId}`}>
          <img
            src={getImageUrl(product.images?.[0] || product.image)}
            alt={product.title}
            className="w-full h-56 object-cover"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x300?text=Imagen+no+disponible';
            }}
          />
          </Link>
          {/* Favorite heart */}
          <button
            onClick={handleToggleFavorite}
            className="absolute top-2 right-2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm hover:bg-white transition-colors z-10"
            data-testid={`fav-btn-${productId}`}
          >
            <Heart className={`w-4 h-4 ${liked ? 'fill-red-500 text-red-500' : 'text-gray-500'}`} />
          </button>
          {isAd && (
            <Badge className="absolute top-2 left-2 bg-blue-600 hover:bg-blue-700 text-white text-xs">
              Patrocinado
            </Badge>
          )}
          {off > 0 && (
            <Badge className={`absolute top-2 ${isAd ? 'left-24' : 'left-2'} bg-red-600 hover:bg-red-600 text-white font-bold shadow`} data-testid={`offer-badge-${productId}`}>
              -{off}%
            </Badge>
          )}
          {product.fullDiscount && (
            <Badge className="absolute top-2 right-2 bg-zl-orange hover:bg-zl-orange-600 text-xs">
              FULL
            </Badge>
          )}
        </div>
        <div className="p-4 flex flex-col flex-1">
          <Link to={`/product/${productSlug}`} onClick={handleClick} data-testid={`product-card-title-link-${productId}`}><h3 className="text-sm text-gray-600 mb-1 line-clamp-2 h-10">{product.title}</h3></Link>

          <div className="mt-2">
            {hasVariants && <p className="text-[11px] text-gray-500">Desde</p>}
            <PriceBlock price={product.price} compare={compare} testid={`card-price-${productId}`} />
          </div>

          {formattedTime && (
            <div className="flex items-center mt-3 px-3 py-2 bg-gradient-to-r from-zl-orange-50 to-zl-yellow-50 rounded-lg border border-zl-orange-200">
              <Clock className="w-5 h-5 text-zl-orange mr-2 flex-shrink-0" />
              <span className="text-sm font-semibold text-zl-brown">
                A {formattedTime} de tu puerta
              </span>
            </div>
          )}

          {product.distance_km && (
            <p className="text-xs text-gray-400 mt-1 text-center">
              A {product.distance_km} km de distancia
            </p>
          )}

          {product.freeShipping && (
            <div className="flex items-center mt-2 text-green-600 text-sm font-medium">
              <Truck className="w-4 h-4 mr-1" />
              Envio gratis
            </div>
          )}

          {product.rating > 0 && (
            <div className="flex items-center mt-2 text-sm text-gray-600">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 mr-1" />
              <span className="font-medium mr-1">{product.rating}</span>
              <span className="text-gray-400">({product.reviews})</span>
            </div>
          )}

          {/* Add to Cart button */}
          <div className="mt-auto pt-3">
            <Button
              variant="outline"
              size="sm"
              className="w-full border-orange-400 text-orange-600 hover:bg-orange-50 hover:text-orange-700 font-medium"
              onClick={handleAddToCart}
              disabled={Number(product.stock) < 1}
              data-testid={`quick-add-cart-${productId}`}
            >
              <ShoppingCart className="w-4 h-4 mr-1.5" />
              {hasVariants ? 'Elegir opción' : 'Agregar'}
            </Button>
          </div>
        </div>
      </Card>
    {hasVariants && <VariantSelectionModal product={product} open={variantOpen} onOpenChange={setVariantOpen} />}
    </>
  );
};

export default ProductCard;
